import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios from 'axios'
import type {
  AiRecommendationResponse,
  ClickUpField,
  ClickUpFolder,
  ClickUpList,
  ClickUpSpace,
  DeployTarget,
  FullStrategy,
  Strategy,
  StrategyItem,
} from 'components/StrategyBuilder/types'

const BASE = `${baseUrl}/api/strategy-builder`
const KEY = ['strategy-builder']

// ── Strategy CRUD ────────────────────────────────────────────────────────────

export const useStrategies = () =>
  useQuery<Strategy[]>({
    queryKey: KEY,
    queryFn: () => axios.get<Strategy[]>(BASE).then(r => r.data),
    staleTime: 1000 * 60 * 2,
  })

export const useStrategy = (id: string | null) =>
  useQuery<FullStrategy>({
    queryKey: [...KEY, id],
    queryFn: () => axios.get<FullStrategy>(`${BASE}/${id}`).then(r => r.data),
    enabled: !!id,
    staleTime: 1000 * 60,
  })

export const useCreateStrategy = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Strategy>) => axios.post<Strategy>(BASE, data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export const useUpdateStrategy = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<Strategy>) =>
      axios.put<Strategy>(`${BASE}/${id}`, data).then(r => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: [...KEY, vars.id] })
    },
  })
}

export const useDeleteStrategy = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => axios.delete(`${BASE}/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

// ── Strategy Items ───────────────────────────────────────────────────────────

export const useAddStrategyItems = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ strategyId, items }: { strategyId: string; items: Partial<StrategyItem>[] }) =>
      axios.post(`${BASE}/${strategyId}/items`, { items }).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: [...KEY, vars.strategyId] }),
  })
}

export const useUpdateStrategyItem = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ strategyId, itemId, ...data }: { strategyId: string; itemId: string } & Partial<StrategyItem>) =>
      axios.put(`${BASE}/${strategyId}/items/${itemId}`, data).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: [...KEY, vars.strategyId] }),
  })
}

export const useRemoveStrategyItem = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ strategyId, itemId }: { strategyId: string; itemId: string }) =>
      axios.delete(`${BASE}/${strategyId}/items/${itemId}`).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: [...KEY, vars.strategyId] }),
  })
}

// ── AI Recommendations ───────────────────────────────────────────────────────

export const useStrategyRecommend = () =>
  useMutation({
    mutationFn: ({ strategyId, listIds }: { strategyId: string; listIds?: string[] }) =>
      axios
        .post<AiRecommendationResponse>(`${BASE}/${strategyId}/recommend`, { listIds })
        .then(r => r.data),
  })

// ── Document Upload ──────────────────────────────────────────────────────────

export const useUploadDocument = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ strategyId, file, url }: { strategyId: string; file?: File; url?: string }) => {
      if (url) {
        return axios.post(`${BASE}/${strategyId}/documents`, { url }).then(r => r.data)
      }
      const formData = new FormData()
      if (file) formData.append('file', file)
      return axios
        .post(`${BASE}/${strategyId}/documents`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(r => r.data)
    },
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: [...KEY, vars.strategyId] }),
  })
}

export const useRemoveDocument = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ strategyId, docId }: { strategyId: string; docId: string }) =>
      axios.delete(`${BASE}/${strategyId}/documents/${docId}`).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: [...KEY, vars.strategyId] }),
  })
}

// ── ClickUp Browser ──────────────────────────────────────────────────────────

export const useClickUpSpaces = () =>
  useQuery<ClickUpSpace[]>({
    queryKey: [...KEY, 'clickup', 'spaces'],
    queryFn: () => axios.get<ClickUpSpace[]>(`${BASE}/clickup/spaces`).then(r => r.data),
    staleTime: 1000 * 60 * 5,
    enabled: false, // manual trigger
  })

export const useClickUpFolders = (spaceId: string | null) =>
  useQuery<ClickUpFolder[]>({
    queryKey: [...KEY, 'clickup', 'folders', spaceId],
    queryFn: () => axios.get<ClickUpFolder[]>(`${BASE}/clickup/spaces/${spaceId}/folders`).then(r => r.data),
    enabled: !!spaceId,
    staleTime: 1000 * 60 * 5,
  })

export const useClickUpLists = (folderId: string | null) =>
  useQuery<ClickUpList[]>({
    queryKey: [...KEY, 'clickup', 'lists', folderId],
    queryFn: () => axios.get<ClickUpList[]>(`${BASE}/clickup/folders/${folderId}/lists`).then(r => r.data),
    enabled: !!folderId,
    staleTime: 1000 * 60 * 5,
  })

export const useClickUpFields = (listId: string | null) =>
  useQuery<ClickUpField[]>({
    queryKey: [...KEY, 'clickup', 'fields', listId],
    queryFn: () => axios.get<ClickUpField[]>(`${BASE}/clickup/lists/${listId}/fields`).then(r => r.data),
    enabled: !!listId,
    staleTime: 1000 * 60 * 5,
  })

export const useClickUpMembers = (spaceId: string | null) =>
  useQuery<any[]>({
    queryKey: [...KEY, 'clickup', 'members', spaceId],
    queryFn: () => axios.get<any[]>(`${BASE}/clickup/spaces/${spaceId}/members`).then(r => r.data),
    enabled: !!spaceId,
    staleTime: 1000 * 60 * 10,
  })

export const useCreateClickUpFolder = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ spaceId, name }: { spaceId: string; name: string }) =>
      axios
        .post<ClickUpFolder>(`${BASE}/clickup/spaces/${spaceId}/folders`, { name })
        .then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'clickup', 'folders'] }),
  })
}

export const useCreateClickUpList = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ folderId, name }: { folderId: string; name: string }) =>
      axios
        .post<ClickUpList>(`${BASE}/clickup/folders/${folderId}/lists`, { name })
        .then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'clickup', 'lists'] }),
  })
}

// ── Deploy ───────────────────────────────────────────────────────────────────

export const useDeployStrategy = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ strategyId, targets, userId }: { strategyId: string; targets: DeployTarget[]; userId?: string }) =>
      axios.post(`${BASE}/${strategyId}/deploy`, { targets, userId }).then(r => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: [...KEY, vars.strategyId] })
    },
  })
}

// ── AI Sherpa Chat ──────────────────────────────────────────────────────────

export const useSherpaChat = () =>
  useMutation({
    mutationFn: ({
      strategyId,
      message,
      history,
      cartItems,
      config,
      recommendations,
    }: {
      strategyId: string
      message: string
      history: Array<{ role: string; content: string }>
      cartItems?: any[]
      config?: any
      recommendations?: any[]
    }) =>
      axios
        .post<{ response: string }>(`${BASE}/${strategyId}/sherpa`, {
          message,
          history,
          cartItems,
          config,
          recommendations,
        })
        .then(r => r.data),
  })

// ── Client History ───────────────────────────────────────────────────────────

export const useFetchClientHistory = () =>
  useMutation({
    mutationFn: ({ listIds, days }: { listIds: string[]; days?: number }) =>
      axios.post(`${BASE}/client-history`, { listIds, days }).then(r => r.data),
  })

// ── ClickUp Template & Client Structure Types ───────────────────────────────

interface ClickUpTemplateTask {
  id: string
  name: string
  description: string
  points: number | null
  status: string
  priority: string | null
  tags: string[]
  customFieldNames: string[]
}

interface ClickUpTemplate {
  listId: string
  listName: string
  taskCount: number
  statuses: string[]
  tasks: ClickUpTemplateTask[]
}

interface ClientFolderInfo {
  folderId: string
  folderName: string
  prefix: string
  listsPresent: string[]
  listsMissing: string[]
  totalTasks: number
  isComplete: boolean
}

interface ClientStructureResult {
  spaceId: string
  spaceName: string
  clients: ClientFolderInfo[]
  totalClients: number
  standardListNames: string[]
}

interface ClientAuditResult {
  folderId: string
  folderName: string
  prefix: string
  lists: {
    id: string
    name: string
    listName?: string
    taskCount: number
    statuses: string[]
    customFields?: { id: string; name: string; type: string }[]
    customFieldCount?: number
  }[]
  listsPresent: string[]
  listsMissing: string[]
  isComplete: boolean
  customFieldAudit?: {
    presentKeyFields: string[]
    missingKeyFields: string[]
  }
}

// ── ClickUp Templates ───────────────────────────────────────────────────────

export const useClickUpTemplates = () =>
  useQuery<ClickUpTemplate[]>({
    queryKey: [...KEY, 'clickup', 'templates'],
    queryFn: () => axios.get<ClickUpTemplate[]>(`${BASE}/clickup/templates`).then(r => r.data),
    staleTime: 1000 * 60 * 10,
    enabled: false, // manual trigger
  })

export const useRefreshTemplates = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => axios.get(`${BASE}/clickup/templates/refresh`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, 'clickup', 'templates'] }),
  })
}

export const useClickUpTemplateTasks = (listId: string | null) =>
  useQuery<ClickUpTemplateTask[]>({
    queryKey: [...KEY, 'clickup', 'templates', listId, 'tasks'],
    queryFn: () => axios.get<ClickUpTemplateTask[]>(`${BASE}/clickup/templates/${listId}/tasks`).then(r => r.data),
    enabled: !!listId,
    staleTime: 1000 * 60 * 5,
  })

// ── Client Structure & Audit ────────────────────────────────────────────────

export const useClientStructure = (spaceId: string | null) =>
  useQuery<ClientStructureResult>({
    queryKey: [...KEY, 'clickup', 'client-structure', spaceId],
    queryFn: () => axios.get<ClientStructureResult>(`${BASE}/clickup/client-structure/${spaceId}`).then(r => r.data),
    enabled: !!spaceId,
    staleTime: 1000 * 60 * 5,
  })

export const useClientAudit = (folderId: string | null) =>
  useQuery<ClientAuditResult>({
    queryKey: [...KEY, 'clickup', 'client', folderId, 'audit'],
    queryFn: () => axios.get<ClientAuditResult>(`${BASE}/clickup/client/${folderId}/audit`).then(r => r.data),
    enabled: !!folderId,
    staleTime: 1000 * 60 * 5,
  })

// ── Points Dashboard ─────────────────────────────────────────────────────────

export interface PointsOverviewClient {
  clientName: string
  strategyCount: number
  totalPoints: number
  totalValue: number
  totalItems: number
  serviceLines: string[]
  latestStatus: string
  perServiceLine: { line: string; allocated: number; items: number }[]
}

export interface PointsOverview {
  totalClients: number
  totalAllocated: number
  totalValue: number
  totalStrategies: number
  clients: PointsOverviewClient[]
  serviceLineSummary: { line: string; allocated: number; items: number }[]
}

export interface ClientUsage {
  folderId: string
  totalUsed: number
  totalTasks: number
  completedTasks: number
  lists: {
    listId: string
    listName: string
    serviceLine: string
    totalTasks: number
    completedTasks: number
    pointsUsed: number
  }[]
  perServiceLine: { line: string; pointsUsed: number; totalTasks: number; completedTasks: number }[]
}

export const usePointsOverview = () =>
  useQuery<PointsOverview>({
    queryKey: [...KEY, 'points', 'overview'],
    queryFn: () => axios.get<PointsOverview>(`${BASE}/points/overview`).then(r => r.data),
    staleTime: 1000 * 60 * 5,
  })

export const useClientUsage = (folderId: string | null) =>
  useQuery<ClientUsage>({
    queryKey: [...KEY, 'points', 'client', folderId, 'usage'],
    queryFn: () => axios.get<ClientUsage>(`${BASE}/points/client/${folderId}/usage`).then(r => r.data),
    enabled: !!folderId,
    staleTime: 1000 * 60 * 5,
  })
